import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { useNavigation } from "../../utils/navigation";
import { AuthUserContext } from "../../context/AuthUserContext";
import { useTranslation } from "react-i18next";

const Container = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background-color: var(--bg-200);
`;
const RadioButton = styled.div`
  margin: var(--spacing-md);
  min-width: 16px;
  min-height: 16px;
  max-width: 16px;
  max-height: 16px;
  border-radius: 20px;
  border: 1px solid var(--primary-200);

  display: none;
  @media (min-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;
const CheckedRadio = styled.div`
  background-color: var(--primary-200);
  width: 8px;
  height: 8px;
  border-radius: 20px;
`;

const LeftDiv = styled.div`
  width: 100%;
  padding: var(--spacing-md);
`;

const PasswordTitle = styled.h3`
  font-weight: 500;
  font-size: var(--font-size-large);
  text-transform: uppercase;
`;

const P = styled.p`
  font-size: var(--font-size-small);
  line-height: var(--font-size-large);
  font-weight: 500;
`;

const AddressType = styled.p`
  font-size: var(--font-size-small);
  font-family: "Larken";
  margin-top: 0;
  border: 1px solid var(--text-200);
  padding: var(--spacing-xs) var(--spacing-md);
  border-radius: 20px;
  text-transform: capitalize;
  color: ${({ $isActive }) =>
    $isActive ? "var(--accent-200)" : "var(--text-200)"};

  border-color: ${({ $isActive }) =>
    $isActive ? "var(--accent-200)" : "var(--text-200)"};
  background-color: var(--bg-100);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  @media (min-width: 767px) {
    width: 90%;
  }
  @media (min-width: 768px) and (max-width: 1040px) {
    width: 100%;
  }
`;
const FormDiv = styled.div`
  width: 100%;
  min-width: 100%;
  @media (min-width: 767px) {
    display: flex;
    gap: 24px;
  }
`;
const FormPart = styled.div``;
const FormFlex = styled.div`
  display: flex;
  gap: var(--spacing-xxs);
  width: calc(100%);
  align-items: flex-start;

  @media (min-width: 767px) {
    width: calc(80% + 8px);
    gap: var(--spacing-md);
  }
`;

const Input = styled.input`
  flex: 3;
  width: 100%;
  margin-bottom: var(--spacing-md);
  padding: 14px 0 14px 8px;
  border: 1px solid #d0d5dd;
  &::placeholder {
  }
  &:disabled {
    background-color: var(--bg-300);
    cursor: not-allowed;
  }

  background-color: var(--bg-100);
`;
const SmallInput = styled(Input)`
  flex: 1;
  width: 100%;
`;
const SmallInputDesktop = styled(SmallInput)`
  display: none;
  @media (min-width: 767px) {
    display: block;
  }
`;
const SmallInputMB = styled(SmallInput)`
  display: block;
  @media (min-width: 767px) {
    display: none;
  }
`;

const Button = styled.button`
  border-radius: 0;
  width: 100%;
  white-space: nowrap;
  @media (min-width: 767px) {
    width: 200px;
  }
`;
const CancelButton = styled(Button)`
  width: auto;
  background-color: var(--bg-200);
  border: none;
  color: var(--text-200);
  &:hover {
    color: var(--primary-200);
    background-color: var(--bg-200);
  }
  display: none;
  @media (min-width: 768px) {
    display: inline-block;
  }
`;
const LocationButton = styled.button`
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs);
  margin: var(--spacing-md) 0;
  width: 100%;

  @media (min-width: 768px) {
    width: auto;
  }
`;

const AddressBookForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const { userProfile, createAddressBook, updateAddressBook } =
    useContext(AuthUserContext);

  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [streetName, setStreetName] = useState("");
  const [number, setNumber] = useState("");
  const [extension, setExtension] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [type, setType] = useState("billing"); // Default tip adrese
  const [sameAsBilling, setSameAsBilling] = useState(true);

  const [editAddress, setEditAddress] = useState(null);

  useEffect(() => {
    if (userProfile && userProfile.addresses) {
      if (id) {
        // ➡️ Prvo proveri da li ID postoji
        const foundAddress = userProfile.addresses.find(
          (address) => address.id === parseInt(id)
        );
        if (foundAddress) {
          setEditAddress(foundAddress);
        } else {
          navigate(-1); // Ako ID postoji ali adresa nije nađena, tek tada izbacuj
        }
      }
    }
  }, [userProfile, id, navigate]);

  useEffect(() => {
    if (editAddress) {
      setCountry(editAddress.country || "");
      setCity(editAddress.city || "");
      setPostalCode(editAddress.postal_code || "");
      setStreetName(editAddress.street_name || "");
      setNumber(editAddress.number || "");
      setExtension(editAddress.extension || "");
      setPhoneNumber(editAddress.phone_number || "");
      setType(editAddress.type || "billing");
    } else {
      // Reset form
      setCountry("");
      setCity("");
      setPostalCode("");
      setStreetName("");
      setNumber("");
      setExtension("");
      setPhoneNumber("");
      setType("billing");
    }
  }, [editAddress]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const addressData = {
      country,
      city,
      postal_code: postalCode,
      street_name: streetName,
      number,
      extension: extension || null,
      phone_number: phoneNumber,
      type,
      is_primary: true,
    };

    try {
      if (!editAddress) {
        // Kreiraj glavnu adresu
        await createAddressBook(addressData);

        // Ako je čekirano "sameAsBilling", kreiraj još jednu adresu (suprotni type)
        if (sameAsBilling) {
          const otherAddressData = {
            ...addressData,
            type: addressData.type === "billing" ? "shipping" : "billing",
          };
          await createAddressBook(otherAddressData);
        }
      } else {
        // Update postojeće adrese
        await updateAddressBook(editAddress.id, addressData);
      }

      navigate(-1); // Vrati nazad nakon snimanja
    } catch (error) {
      console.error(error);
    }
  };

  const handleCancelClick = () => {
    navigate(-1);
  };

  return (
    <Container>
      <LeftDiv>
        <PasswordTitle>
          {editAddress
            ? t("ADDRESS_BOOK.EDIT_ADDRESS")
            : t("ADDRESS_BOOK.ADD_NEW_ADDRESS")}
        </PasswordTitle>
        <Form onSubmit={handleSubmit}>
          <FormPart>
            {userProfile && (
              <P>
                {userProfile.first_name} {userProfile.last_name}
              </P>
            )}
            <FormFlex>
              <Input
                type="text"
                placeholder={t("LABELS.PHONE_NUMBER")}
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                required
              />
              <Input
                type="text"
                placeholder={t("LABELS.COUNTRY")}
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                required
              />
            </FormFlex>

            <FormFlex>
              <SmallInput
                type="text"
                placeholder={t("LABELS.POSTAL_CODE")}
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                required
              />
              <Input
                type="text"
                placeholder={t("LABELS.CITY")}
                value={city}
                onChange={(e) => setCity(e.target.value)}
                required
              />
            </FormFlex>

            <FormFlex>
              <Input
                type="text"
                placeholder={t("LABELS.STREET")}
                value={streetName}
                onChange={(e) => setStreetName(e.target.value)}
                required
              />
              <SmallInput
                type="text"
                placeholder={t("LABELS.STREET_NUMBER")}
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                required
              />
            </FormFlex>

            <FormFlex>
              <Input
                type="text"
                placeholder={`${t("LABELS.EXTENSION")} (${t(
                  "LABELS.OPTIONAL"
                )})`}
                value={extension}
                onChange={(e) => setExtension(e.target.value)}
              />
            </FormFlex>

            <FormFlex style={{ flexWrap: "wrap" }}>
              {!editAddress && (
                <>
                  <PasswordTitle>{t("ADDRESS_BOOK.TYPE")}</PasswordTitle>
                  <label>
                    <input
                      type="checkbox"
                      checked={sameAsBilling}
                      onChange={(e) => setSameAsBilling(e.target.checked)}
                    />
                    {t("ADDRESS_BOOK.SAME_AS_BILLING")}
                  </label>
                </>
              )}
              {!sameAsBilling && (
                <>
                  <AddressType
                    $isActive={type === "billing"}
                    onClick={() => setType("billing")}
                  >
                    {t("ADDRESS_BOOK.BILLING")}
                  </AddressType>
                  <AddressType
                    $isActive={type === "shipping"}
                    onClick={() => setType("shipping")}
                  >
                    {t("ADDRESS_BOOK.SHIPPING")}
                  </AddressType>
                </>
              )}
            </FormFlex>
          </FormPart>

          <FormFlex style={{ margin: "14px 0" }}>
            <Button type="submit">{t("BUTTONS.SAVE_AND_DELIVER_HERE")}</Button>
            {userProfile?.addresses.length === 0 && (
              <CancelButton type="button" onClick={handleCancelClick}>
                {t("BUTTONS.CANCEL")}
              </CancelButton>
            )}
          </FormFlex>
        </Form>
      </LeftDiv>
    </Container>
  );
};

export default AddressBookForm;
